// Slide-in "Assignment Builder" — teacher packages the selected practice items
// into ONE grouped assignment (title, description, due date). Anchored to the
// right edge of the viewport; the underlying library page stays visible so the
// teacher can eyeball the picks. Modeled on FrenchPrep's drawer to match the
// muscle memory of instructors migrating between platforms.
import React, { useState } from 'react';
import { Loader2, X } from 'lucide-react';

import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

const EXAM_LABEL = { celpip: 'CELPIP', pte: 'PTE Core' };
const SECTION_LABEL = { listening: 'Listening', reading: 'Reading', writing: 'Writing', speaking: 'Speaking' };

function todayPlus(days) {
  const d = new Date(); d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export default function AssignmentBuilder({ student, items, onClose, onCreated, onClear, onRemoveItem }) {
  const [title, setTitle] = useState(`${student.name.split(' ')[0]} — ${new Date().toLocaleDateString()}`);
  const [description, setDescription] = useState('');
  const [dueAt, setDueAt] = useState(todayPlus(7));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Grouped counts by section for the summary strip
  const bySection = items.reduce((acc, it) => {
    acc[it.section] = (acc[it.section] || 0) + 1;
    return acc;
  }, {});

  const canSubmit = title.trim().length > 0 && items.length > 0 && !saving;

  const save = async () => {
    if (!canSubmit) return;
    setSaving(true); setError('');
    try {
      const body = {
        title: title.trim(),
        description: description.trim() || null,
        due_at: dueAt ? new Date(dueAt).toISOString() : null,
        items: items.map((it) => ({ exam: it.exam, task_type: it.task_type, set_id: it.set_id })),
      };
      const res = await api.orgCreateAssignment(student.user_id, body);
      onCreated && onCreated(res.data.assignment);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not create the assignment.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-slate-900/40" onClick={onClose} data-testid="builder-backdrop" />
      <aside
        className="fixed right-0 top-0 z-50 flex h-screen w-full max-w-md flex-col border-l border-slate-200 bg-white shadow-xl"
        data-testid="assignment-builder"
      >
        <header className="border-b border-slate-200 px-6 py-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="mono-stat">Assignment builder</p>
              <h2 className="font-display text-lg font-semibold text-slate-900">For {student.name}</h2>
              <p className="text-xs text-slate-500">This will be sent to this student only.</p>
            </div>
            <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-900" data-testid="builder-close" aria-label="Close">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="mt-3 flex items-center justify-between text-xs">
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(bySection).map(([sec, n]) => (
                <span key={sec} className="rounded-full bg-slate-100 px-2 py-0.5 font-medium capitalize text-slate-700">
                  {SECTION_LABEL[sec] || sec}: {n}
                </span>
              ))}
              {items.length === 0 && <span className="text-slate-400">No items selected yet</span>}
            </div>
            {items.length > 0 && (
              <button type="button" onClick={onClear} className="text-slate-500 hover:text-rose-600" data-testid="builder-clear">Clear all</button>
            )}
          </div>
        </header>

        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
          <label className="block">
            <span className="mono-stat block">Title *</span>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={120}
              placeholder="e.g. Week 1 — Listening & Writing"
              data-testid="builder-title"
              className="mt-1 w-full rounded border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-indigo-400"
            />
          </label>

          <label className="block">
            <span className="mono-stat block">Description (optional)</span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              maxLength={800}
              placeholder="Instructions or notes for the student."
              data-testid="builder-description"
              className="mt-1 w-full rounded border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-indigo-400"
            />
          </label>

          <label className="block">
            <span className="mono-stat block">Due date (optional)</span>
            <input
              type="date"
              value={dueAt}
              onChange={(e) => setDueAt(e.target.value)}
              data-testid="builder-due"
              className="mt-1 rounded border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-indigo-400"
            />
          </label>

          <div>
            <p className="mono-stat">Selected items ({items.length})</p>
            {items.length === 0 ? (
              <p className="mt-2 text-sm text-slate-500">Pick items from the library — they&apos;ll appear here.</p>
            ) : (
              <ul className="mt-2 divide-y divide-slate-100 rounded border border-slate-200">
                {items.map((it) => (
                  <li key={it.set_id} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`builder-item-${it.set_id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-slate-900">{it.title}</p>
                      <p className="text-xs text-slate-500">
                        {EXAM_LABEL[it.exam] || it.exam} · <span className="capitalize">{it.section}</span>
                      </p>
                    </div>
                    <button type="button" onClick={() => onRemoveItem(it.set_id)}
                      data-testid={`builder-remove-${it.set_id}`}
                      className="text-slate-400 hover:text-rose-600" aria-label="Remove item">
                      <X className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {error && <p className="text-sm text-rose-600" data-testid="builder-error">{error}</p>}
        </div>

        <footer className="border-t border-slate-200 bg-slate-50 px-6 py-3">
          <button
            type="button"
            onClick={save}
            disabled={!canSubmit}
            data-testid="builder-submit"
            className={cn(
              'inline-flex w-full items-center justify-center gap-2 rounded px-4 py-2.5 text-sm font-semibold',
              canSubmit ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'cursor-not-allowed bg-slate-200 text-slate-500'
            )}
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Assign to {student.name.split(' ')[0]}
          </button>
        </footer>
      </aside>
    </>
  );
}
